<template>
  <b-modal
    :visible="value"
    :title="modalTitle"
    size="lg"
    scrollable
    no-close-on-backdrop
    @hidden="handleHidden"
    @ok="handleSubmit"
  >
    <validation-observer
      ref="formValidator"
      v-slot="{ invalid }"
    >
      <b-form @submit.prevent="handleSubmit">
        <!-- Name and Code on same row -->
        <b-row>
          <b-col
            cols="12"
            md="8"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Department Name"
              rules="required|max:255"
            >
              <b-form-group
                label="Department Name"
                label-for="name"
                :state="errors.length > 0 ? false : null"
              >
                <b-form-input
                  id="name"
                  v-model="formData.name"
                  placeholder="Enter department name"
                  :state="errors.length > 0 ? false : null"
                />
                <b-form-invalid-feedback>
                  {{ errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
          </b-col>
          <b-col
            cols="12"
            md="4"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Code"
              rules="max:50"
            >
              <b-form-group
                label="Department Code"
                label-for="code"
                :state="errors.length > 0 ? false : null"
              >
                <b-form-input
                  id="code"
                  v-model="formData.code"
                  placeholder="Auto-generated if empty"
                  :state="errors.length > 0 ? false : null"
                />
                <b-form-text>Leave empty for automatic code generation</b-form-text>
                <b-form-invalid-feedback>
                  {{ errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
          </b-col>
        </b-row>

        <!-- Description -->
        <validation-provider
          v-slot="{ errors }"
          name="Description"
          rules="max:1000"
        >
          <b-form-group
            label="Description"
            label-for="description"
            :state="errors.length > 0 ? false : null"
          >
            <b-form-textarea
              id="description"
              v-model="formData.description"
              rows="3"
              placeholder="Enter department description"
              :state="errors.length > 0 ? false : null"
            />
            <b-form-invalid-feedback>
              {{ errors[0] }}
            </b-form-invalid-feedback>
          </b-form-group>
        </validation-provider>

        <!-- Parent Department -->
        <b-form-group
          label="Parent Department"
          label-for="parent"
        >
          <v-select
            id="parent"
            v-model="formData.parent_id"
            :options="availableDepartments"
            :reduce="dept => dept.id"
            label="full_path"
            placeholder="Select parent department (leave empty for root)"
            :loading="loadingDepartments"
            :disabled="loadingDepartments"
            clearable
          >
            <template #no-options="{ search, searching }">
              <template v-if="searching">
                No results found for <em>{{ search }}</em>.
              </template>
              <em v-else>Start typing to search...</em>
            </template>
          </v-select>
          <b-form-text>Leave empty to create a root department</b-form-text>
        </b-form-group>

        <!-- Manager -->
        <b-form-group
          label="Department Manager"
          label-for="manager"
        >
          <v-select
            id="manager"
            v-model="formData.manager_id"
            :options="availableEmployees"
            :reduce="emp => emp.id"
            :get-option-label="getEmployeeLabel"
            placeholder="Select department manager"
            :loading="loadingEmployees"
            :disabled="loadingEmployees"
            clearable
          >
            <template #option="option">
              <div>
                <strong>{{ option.full_name }}</strong>
                <br>
                <small class="text-muted">{{ option.employee_code }}</small>
              </div>
            </template>
            <template #no-options="{ search, searching }">
              <template v-if="searching">
                No results found for <em>{{ search }}</em>.
              </template>
              <em v-else>Start typing to search...</em>
            </template>
          </v-select>
          <b-form-text>Only active employees can be assigned as managers</b-form-text>
        </b-form-group>

        <!-- Status -->
        <b-form-group>
          <b-form-checkbox
            v-model="formData.is_active"
            switch
            size="lg"
          >
            <span class="font-weight-bold">Active Status</span>
            <br>
            <small class="text-muted">
              {{ formData.is_active ? 'Department is active and visible' : 'Department is inactive' }}
            </small>
          </b-form-checkbox>
        </b-form-group>
      </b-form>
    </validation-observer>

    <template #modal-footer="{ ok, cancel }">
      <b-button
        variant="outline-secondary"
        @click="cancel()"
      >
        Cancel
      </b-button>
      <b-button
        variant="primary"
        :disabled="submitting"
        @click="ok()"
      >
        <b-spinner
          v-if="submitting"
          small
          class="mr-50"
        />
        {{ mode === 'create' ? 'Create Department' : 'Update Department' }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import {
  BModal,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormText,
  BFormInvalidFeedback,
  BFormCheckbox,
  BButton,
  BSpinner,
  BRow,
  BCol,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required, max } from '@core/utils/validations/validations'
import { mapActions } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { getDepartments } from '@/api/departments/departments'

export default {
  name: 'DepartmentFormModal',

  components: {
    BModal,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormText,
    BFormInvalidFeedback,
    BFormCheckbox,
    BButton,
    BSpinner,
    BRow,
    BCol,
    vSelect,
    ValidationProvider,
    ValidationObserver,
  },

  props: {
    value: {
      type: Boolean,
      required: true,
    },
    department: {
      type: Object,
      default: null,
    },
    mode: {
      type: String,
      default: 'create',
      validator: val => ['create', 'edit'].includes(val),
    },
  },

  data() {
    return {
      formData: {
        name: '',
        code: '',
        description: '',
        parent_id: null,
        manager_id: null,
        is_active: true,
      },
      availableDepartments: [],
      availableEmployees: [],
      loadingDepartments: false,
      loadingEmployees: false,
      submitting: false,
      required,
      max,
    }
  },

  computed: {
    modalTitle() {
      return this.mode === 'create' ? 'Create New Department' : 'Edit Department'
    },
  },

  watch: {
    value(val) {
      if (val) {
        this.initForm()
        this.loadDepartments()
        this.loadEmployees()
      }
    },
    department: {
      immediate: true,
      handler(val) {
        if (val) {
          this.formData = {
            name: val.name || '',
            code: val.code || '',
            description: val.description || '',
            parent_id: val.parent_id || null,
            manager_id: val.manager_id || null,
            is_active: val.is_active !== false,
          }
        }
      },
    },
  },

  methods: {
    ...mapActions('departments', ['createDepartment', 'updateDepartment']),

    initForm() {
      if (this.mode === 'create') {
        this.formData = {
          name: '',
          code: '',
          description: '',
          parent_id: null,
          manager_id: null,
          is_active: true,
        }
      }
      this.$nextTick(() => {
        if (this.$refs.formValidator) {
          this.$refs.formValidator.reset()
        }
      })
    },

    async loadDepartments() {
      this.loadingDepartments = true
      try {
        const response = await getDepartments({
          per_page: 1000,
          is_active: true,
        })
        this.availableDepartments = response.data.filter(dept => {
          // Exclude current department when editing to prevent self-parenting
          if (this.mode === 'edit' && this.department) {
            return dept.id !== this.department.id
          }
          return true
        })
      } catch (error) {
        console.error('Error loading departments:', error)
      } finally {
        this.loadingDepartments = false
      }
    },

    async loadEmployees() {
      this.loadingEmployees = true
      try {
        // Using employees API endpoint - adjust based on your actual API
        const response = await this.$axios.post('/employees/list', {
          per_page: 1000,
          filters: {
            status: ['contracted'], // Only contracted employees
          },
        })
        this.availableEmployees = response.data.data || []
      } catch (error) {
        console.error('Error loading employees:', error)
        this.availableEmployees = []
      } finally {
        this.loadingEmployees = false
      }
    },

    getEmployeeLabel(employee) {
      return `${employee.full_name} (${employee.employee_code})`
    },

    async handleSubmit(bvModalEvt) {
      if (bvModalEvt) {
        bvModalEvt.preventDefault()
      }

      console.log('Form submission started', { mode: this.mode, formData: this.formData })

      const valid = await this.$refs.formValidator.validate()
      if (!valid) {
        console.log('Form validation failed')
        return
      }

      this.submitting = true
      try {
        const data = { ...this.formData }

        // Remove empty code if auto-generation is desired
        if (!data.code || data.code.trim() === '') {
          delete data.code
        }

        console.log('Submitting data:', data)

        if (this.mode === 'create') {
          await this.createDepartment(data)
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Success',
              icon: 'CheckIcon',
              text: 'Department created successfully',
              variant: 'success',
            },
          })
        } else {
          await this.updateDepartment({
            id: this.department.id,
            updates: data,
          })
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Success',
              icon: 'CheckIcon',
              text: 'Department updated successfully',
              variant: 'success',
            },
          })
        }

        this.$emit('submitted')
        this.handleHidden()
      } catch (error) {
        console.error('Form submission error:', error)

        // Handle validation errors
        if (error.response?.status === 422) {
          const { errors } = error.response.data
          Object.keys(errors).forEach(field => {
            errors[field].forEach(message => {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: 'Validation Error',
                  icon: 'AlertCircleIcon',
                  text: message,
                  variant: 'danger',
                },
              })
            })
          })
        } else {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error',
              icon: 'AlertTriangleIcon',
              text: error.response?.data?.message || `Failed to ${this.mode} department`,
              variant: 'danger',
            },
          })
        }
      } finally {
        this.submitting = false
      }
    },

    handleHidden() {
      this.$emit('input', false)
      this.initForm()
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@core/scss/vue/libs/vue-select.scss';
</style>
